import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SygnalSignalMap } from "../components/SygnalSignalMap";
import {
  ArrowRight, ShieldCheck, Radio, Cpu, Zap, Server,
  Search, FileLock2, Activity, Lock, ChevronRight, CheckCircle2, AlertTriangle,
} from "lucide-react";

const IMG_INVISIBLE = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/qut6fprp_SYGNALSHIELD_invisible-electronicamente.jpg";
const IMG_FARADAY   = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/zgeqgbss_SYGNALSHIELD_Prefabricated-Faraday-cages-1-1.png";
const IMG_TENT      = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/w15pehci_SYGNALSHIELD_SS_1.jpg";
const IMG_SOLUTIONS = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/kh4amrm7_SIGNALSHIELD_Soluciones.png";
const IMG_LOGO      = "https://customer-assets.emergentagent.com/job_atlas-shield-hub/artifacts/wi9dw8lv_sYGNALSHIELD_SS_logo_.jpg";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || "";

const PRODUCT_LINES = [
  { id: "faraday",  icon: ShieldCheck, titleKey: "lineFaradayTitle",  descKey: "lineFaradayDesc",  tag: "100 dB" },
  { id: "tempest",  icon: Server,      titleKey: "lineTempestTitle",  descKey: "lineTempestDesc",  tag: "TEMPEST/1-92" },
  { id: "hemp",     icon: Zap,         titleKey: "lineHempTitle",     descKey: "lineHempDesc",     tag: "MIL-STD-188-125" },
  { id: "tactical", icon: Cpu,         titleKey: "lineTacticalTitle", descKey: "lineTacticalDesc", tag: "SS-1" },
];

const USE_CASES = [
  { id: "uc1", icon: Server,    tKey: "uc1" },
  { id: "uc2", icon: Search,    tKey: "uc2" },
  { id: "uc3", icon: Radio,     tKey: "uc3" },
  { id: "uc4", icon: FileLock2, tKey: "uc4" },
];

const SPECS = [
  { labelKey: "specAttenuation", valKey: "specAttenuationVal", icon: Activity },
  { labelKey: "specStandards",   valKey: "specStandardsVal",   icon: ShieldCheck },
  { labelKey: "specRange",       valKey: "specRangeVal",       icon: Radio },
  { labelKey: "specCustom",      valKey: "specCustomVal",      icon: Lock },
];

const SygnalShield = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAuth = user && typeof user === "object";

  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", country: "", requirements: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { ok: boolean, msg: string }
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/inquiries/captcha-config`)
      .then((r) => setCaptchaEnabled(Boolean(r?.data?.enabled)))
      .catch(() => setCaptchaEnabled(false));
  }, []);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaEnabled && !captchaToken) {
      setFeedback({ ok: false, msg: t("sygnal.formCaptchaRequired") });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        type: "sygnal_shield",
        captcha_token: captchaToken || undefined,
      });
      setFeedback({ ok: true, msg: t("sygnal.formSuccess") });
      setForm({ name: "", email: "", company: "", phone: "", country: "", requirements: "" });
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }
      setCaptchaToken(null);
    } catch (err) {
      const status = err?.response?.status;
      let msg = t("sygnal.formError");
      if (status === 429) msg = t("sygnal.formRateLimit");
      else if (status === 403) msg = t("sygnal.formCaptchaFailed");
      setFeedback({ ok: false, msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="sygnal-shield-page">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(110deg, rgba(5,5,5,0.96) 0%, rgba(5,5,5,0.78) 45%, rgba(5,5,5,0.45) 75%, rgba(5,5,5,0.7) 100%), url(${IMG_INVISIBLE})`,
            backgroundSize: "cover",
            backgroundPosition: "center right",
          }}
        />
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 lg:col-span-8">
              <div className="flex items-center gap-3">
                <img src={IMG_LOGO} alt="Sygnal Shield" className="h-10 w-auto" data-testid="sygnal-hero-logo" />
                <p className="tactical-label" data-testid="sygnal-eyebrow">
                  <span className="inline-block w-8 h-px bg-[#19A7E0] mr-3 align-middle" />
                  {t("sygnal.eyebrow")}
                </p>
              </div>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[0.95]" data-testid="sygnal-title">
                {t("sygnal.heroTitle")}
              </h1>
              <p className="mt-7 text-base lg:text-lg text-white/70 max-w-2xl leading-relaxed">
                {t("sygnal.heroSubtitle")}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href="#audit-form"
                  data-testid="sygnal-cta-primary"
                  className="inline-flex items-center gap-2 px-6 py-3.5 btn-primary text-sm font-mono uppercase tracking-[0.18em] rounded-sm"
                >
                  {t("sygnal.ctaPrimary")} <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#diagnostic"
                  data-testid="sygnal-cta-secondary"
                  className="inline-flex items-center gap-2 px-6 py-3.5 btn-ghost text-sm font-mono uppercase tracking-[0.18em] rounded-sm"
                >
                  {t("sygnal.ctaSecondary")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIAGNOSTIC: SignalMap + Faraday cage image side by side */}
      <section id="diagnostic" className="relative py-20 lg:py-28 border-t border-white/[0.08]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">◉ LIVE DIAGNOSTIC</p>
            <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight">
              {t("sygnal.mapTitle")}
            </h2>
            <p className="mt-5 text-base text-white/65 leading-relaxed">{t("sygnal.mapDesc")}</p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Animated map (centerpiece) */}
            <div className="lg:col-span-8" data-testid="sygnal-map-wrapper">
              <SygnalSignalMap />
            </div>

            {/* Physical product reference */}
            <div className="lg:col-span-4 flex flex-col gap-6">
              <article className="card-sharp overflow-hidden" data-testid="sygnal-physical-tent">
                <div
                  className="aspect-[4/3] relative"
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0) 50%, rgba(5,5,5,0.95) 100%), url(${IMG_TENT})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="absolute top-4 left-4 tactical-label">SS-1 · TACTICAL</div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#19A7E0]">
                      Field deploy · 80 dB
                    </p>
                  </div>
                </div>
                <div className="p-5 border-t border-white/10">
                  <p className="text-sm text-white/85">{t("sygnal.gal3")}</p>
                </div>
              </article>

              <article className="card-sharp overflow-hidden" data-testid="sygnal-physical-faraday">
                <div
                  className="aspect-[4/3] relative"
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0) 50%, rgba(5,5,5,0.95) 100%), url(${IMG_FARADAY})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="absolute top-4 left-4 tactical-label">PREFAB · FARADAY</div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#19A7E0]">
                      Modular · 100 dB
                    </p>
                  </div>
                </div>
                <div className="p-5 border-t border-white/10">
                  <p className="text-sm text-white/85">{t("sygnal.gal2")}</p>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT LINES */}
      <section className="relative py-24 lg:py-32 border-t border-white/[0.08]" data-testid="sygnal-features">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("sygnal.featuresEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-5xl font-medium tracking-tight">
              {t("sygnal.featuresTitle")}
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.08] border border-white/[0.08]">
            {PRODUCT_LINES.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.id}
                  data-testid={`sygnal-line-${p.id}`}
                  className="bg-[#050505] p-8 lg:p-10 hover:bg-[#0A0D14] transition-colors min-h-[300px] flex flex-col"
                >
                  <Icon className="w-7 h-7 text-[#19A7E0]" />
                  <p className="mt-6 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">
                    {String(i + 1).padStart(2, "0")} / 04
                  </p>
                  <h3 className="mt-3 text-xl font-medium tracking-tight">
                    {t(`sygnal.${p.titleKey}`)}
                  </h3>
                  <p className="mt-4 text-sm text-white/60 leading-relaxed flex-1">
                    {t(`sygnal.${p.descKey}`)}
                  </p>
                  <p className="mt-6 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#19A7E0]">
                    <ShieldCheck className="w-3 h-3" /> {p.tag}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="relative py-24 border-t border-white/[0.08]" data-testid="sygnal-gallery">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("sygnal.galleryEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
              {t("sygnal.galleryTitle")}
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4">
            <figure
              className="md:col-span-7 card-sharp overflow-hidden relative aspect-[16/9]"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0) 50%, rgba(5,5,5,0.95) 100%), url(${IMG_INVISIBLE})`,
                backgroundSize: "cover", backgroundPosition: "center",
              }}
              data-testid="sygnal-gal-1"
            >
              <figcaption className="absolute bottom-4 left-4 right-4">
                <p className="tactical-label">01 · Invisible electronicamente</p>
                <p className="mt-2 text-base text-white/90 max-w-xl">{t("sygnal.gal1")}</p>
              </figcaption>
            </figure>

            <figure
              className="md:col-span-5 card-sharp overflow-hidden relative aspect-[4/3] md:aspect-auto"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0) 50%, rgba(5,5,5,0.95) 100%), url(${IMG_SOLUTIONS})`,
                backgroundSize: "cover", backgroundPosition: "center",
              }}
              data-testid="sygnal-gal-4"
            >
              <figcaption className="absolute bottom-4 left-4 right-4">
                <p className="tactical-label">04 · Soluciones</p>
                <p className="mt-2 text-sm text-white/90">{t("sygnal.gal4")}</p>
              </figcaption>
            </figure>

            <figure
              className="md:col-span-5 card-sharp overflow-hidden relative aspect-[4/3]"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0) 50%, rgba(5,5,5,0.95) 100%), url(${IMG_FARADAY})`,
                backgroundSize: "cover", backgroundPosition: "center",
              }}
              data-testid="sygnal-gal-2"
            >
              <figcaption className="absolute bottom-4 left-4 right-4">
                <p className="tactical-label">02 · Faraday Prefab</p>
                <p className="mt-2 text-sm text-white/90">{t("sygnal.gal2")}</p>
              </figcaption>
            </figure>

            <figure
              className="md:col-span-7 card-sharp overflow-hidden relative aspect-[16/9]"
              style={{
                backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0) 50%, rgba(5,5,5,0.95) 100%), url(${IMG_TENT})`,
                backgroundSize: "cover", backgroundPosition: "center",
              }}
              data-testid="sygnal-gal-3"
            >
              <figcaption className="absolute bottom-4 left-4 right-4">
                <p className="tactical-label">03 · SS-1 Tactical Tent</p>
                <p className="mt-2 text-base text-white/90 max-w-xl">{t("sygnal.gal3")}</p>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="relative py-24 lg:py-32 border-t border-white/[0.08]" data-testid="sygnal-use-cases">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("sygnal.useCasesEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">
              {t("sygnal.useCasesTitle")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {USE_CASES.map(({ id, icon: Icon, tKey }) => (
              <article
                key={id}
                data-testid={`sygnal-uc-${id}`}
                className="card-sharp p-6 lg:p-8 flex items-start gap-5 hover:bg-[#0A0D14] transition-colors"
              >
                <div className="w-12 h-12 flex-shrink-0 bg-[#19A7E0]/10 border border-[#19A7E0]/30 rounded-sm flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#19A7E0]" />
                </div>
                <div>
                  <h3 className="text-lg font-medium tracking-tight">{t(`sygnal.${tKey}Title`)}</h3>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed">{t(`sygnal.${tKey}Desc`)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SPECS */}
      <section className="relative py-24 border-t border-white/[0.08]" data-testid="sygnal-specs">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="tactical-label">{t("sygnal.specEyebrow")}</p>
            <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">{t("sygnal.specTitle")}</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.08] border border-white/[0.08]">
            {SPECS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-[#050505] p-6 lg:p-8" data-testid={`sygnal-spec-${i}`}>
                  <Icon className="w-5 h-5 text-[#19A7E0]" />
                  <p className="mt-4 tactical-label">{t(`sygnal.${s.labelKey}`)}</p>
                  <p className="mt-2 text-xl lg:text-2xl font-medium tracking-tight">{t(`sygnal.${s.valKey}`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AUDIT FORM */}
      <section
        id="audit-form"
        className="relative py-24 lg:py-32 border-t border-white/[0.08]"
        data-testid="sygnal-form-section"
      >
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="tactical-label">{t("sygnal.formEyebrow")}</p>
              <h2 className="mt-4 text-3xl lg:text-4xl font-medium tracking-tight">{t("sygnal.formTitle")}</h2>
              <p className="mt-5 text-base text-white/65 leading-relaxed">{t("sygnal.formDesc")}</p>

              {isAuth && (
                <div className="mt-10 card-sharp p-6 bg-[#0A0D14]" data-testid="sygnal-vault-promo">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-[#19A7E0] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">{t("sygnal.vaultPromoTitle")}</p>
                      <p className="mt-2 text-sm text-white/60 leading-relaxed">{t("sygnal.vaultPromoDesc")}</p>
                      <Link
                        to="/dashboard/sygnal-vault"
                        data-testid="sygnal-vault-link"
                        className="mt-4 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-[#19A7E0] hover:text-white transition-colors"
                      >
                        {t("sygnal.vaultPromoCta")} <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <form
              onSubmit={handleSubmit}
              className="lg:col-span-7 card-sharp p-6 lg:p-8 bg-[#0A0D14]"
              data-testid="sygnal-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("sygnal.formName")} testid="form-name" required
                       value={form.name} onChange={handleChange("name")} />
                <Field label={t("sygnal.formEmail")} testid="form-email" required type="email"
                       value={form.email} onChange={handleChange("email")} />
                <Field label={t("sygnal.formCompany")} testid="form-company"
                       value={form.company} onChange={handleChange("company")} />
                <Field label={t("sygnal.formPhone")} testid="form-phone"
                       value={form.phone} onChange={handleChange("phone")} />
                <Field label={t("sygnal.formCountry")} testid="form-country"
                       value={form.country} onChange={handleChange("country")} />
              </div>

              <div className="mt-4">
                <label className="block tactical-label mb-2">{t("sygnal.formRequirements")}</label>
                <textarea
                  required
                  data-testid="form-requirements"
                  value={form.requirements}
                  onChange={handleChange("requirements")}
                  placeholder={t("sygnal.formRequirementsPh")}
                  rows={5}
                  className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#19A7E0] focus:outline-none rounded-sm"
                />
              </div>

              {captchaEnabled && HCAPTCHA_SITE_KEY && (
                <div className="mt-5" data-testid="form-captcha">
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={HCAPTCHA_SITE_KEY}
                    theme="dark"
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 btn-primary text-sm font-mono uppercase tracking-[0.18em] rounded-sm disabled:opacity-60"
              >
                {submitting ? t("sygnal.formSending") : t("sygnal.formSubmit")}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>

              {feedback && (
                <div
                  data-testid={feedback.ok ? "form-success" : "form-error"}
                  className={`mt-5 flex items-start gap-2 text-sm ${
                    feedback.ok ? "text-[#0E9F6E]" : "text-[#FF6A3D]"
                  }`}
                >
                  {feedback.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
                  <span>{feedback.msg}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const Field = ({ label, testid, value, onChange, type = "text", required = false }) => (
  <div>
    <label className="block tactical-label mb-2">{label}{required && <span className="text-[#19A7E0]"> *</span>}</label>
    <input
      type={type}
      required={required}
      data-testid={testid}
      value={value}
      onChange={onChange}
      className="w-full bg-[#050505] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#19A7E0] focus:outline-none rounded-sm"
    />
  </div>
);

export default SygnalShield;
